import { apiClient } from "./client";
import { TipoServizioPrecredit } from "./devices";

/**
 * «Stato lavori» (issue #211): le pratiche, una per coppia cliente–cassaforte,
 * con lo stato dei loro passi. Solo admin e staff (`stato_lavori:read` e
 * `stato_lavori:update`).
 *
 * Lo stato di ogni passo, l'avanzamento, la fase, le voci mancanti del passo 8
 * e la scadenza del preavviso li calcola il backend (`services/statoLavori/`):
 * qui si mostrano, non si ricalcolano. Anche il catalogo (titoli, stati,
 * spunte, indirizzi della banca) arriva dentro la risposta.
 *
 * Le date senza ora sono chiavi `AAAA-MM-GG`: si leggono dai pezzi della
 * stringa, mai con `new Date()` (vedi `pages/StatoLavori/presentazione.ts`).
 */

export type StatoBase = "da_fare" | "in_corso" | "completato" | "non_necessario" | "bloccato";

export type TipoPasso =
  | "documenti_cliente"
  | "autorizzazione_banca"
  | "conto_tecnico"
  | "codice_gdo"
  | "contratto"
  | "allestimento_sistemi"
  | "sopralluogo"
  | "installazione"
  | "documenti_vigilanza"
  | "attivazione";

export type ModuloRecord = "sopralluogo" | "installazione" | "attivazione";

/** Il passo di una bozza: 7 (installazione) o 9 (attivazione). */
export type TipoBozza = "installazione" | "attivazione";

export interface Responsabile {
  tipo: "persona" | "organizzazione" | "testo";
  nome: string;
  /** L'utente, quando è una persona dell'app. */
  id: string | null;
}

export interface Segnalazione {
  codice: "chiusa" | "bloccata" | "servizio_da_verificare" | "da_verificare" | "installare_entro_superato" | "promemoria_ordini";
  testo: string;
}

export interface Cassaforte {
  id: string;
  device_id: string | null;
  matricola: string | null;
  etichetta: string | null;
  codice_gdo: string | null;
}

/** Una riga dell'elenco: anche la testa del dettaglio. */
export interface RigaPratica {
  id: string;
  cliente: { id: string; nome: string };
  sede: { id: string; nome: string | null; citta: string | null };
  cassaforte: Cassaforte;
  condivisa_con: { pratica_id: string; cliente: { id: string; nome: string }; sede: { id: string; nome: string | null } }[];
  servizio: { valore: TipoServizioPrecredit | null; da_verificare: boolean; motivo: string | null };
  fase: { tipo: TipoPasso; titolo: string; stato: StatoBase; dettaglio: string } | null;
  avanzamento: { completati: number; necessari: number; non_necessari: number };
  installare_entro: string | null;
  prossimo_passo: { azione: string; responsabile: Responsabile | null } | null;
  segnalazioni: Segnalazione[];
  chiusa: boolean;
  bloccata: boolean;
  completata: boolean;
  avviata_il: string;
  /** Bozze da confermare sui passi 7 e 9 della pratica (PR C1). */
  bozze_da_confermare: number;
}

export interface ElencoPratiche {
  righe: RigaPratica[];
  /** Contano le pratiche in corso, qualunque sia il filtro. */
  conteggi: { in_corso: number; bloccate: number; servizio_da_verificare: number; con_promemoria_ordini: number; con_bozze: number };
  etichette_stato: Record<StatoBase, string>;
}

export type FiltroStato = "in_corso" | "completate" | "chiuse" | "tutte";
export type FiltroSegnalazione = "bloccate" | "da_verificare" | "promemoria_ordini" | "da_confermare";

export interface ParametriElenco {
  stato?: FiltroStato;
  filtro?: FiltroSegnalazione | null;
  /** Un servizio, oppure «da_verificare». */
  servizio?: TipoServizioPrecredit | "da_verificare" | null;
  q?: string;
}

export interface StatoIntermedio {
  codice: string;
  etichetta: string;
  raggiunto: boolean;
  il: string | null;
  fonte: "manuale" | "odoo" | "ricavato" | null;
  odoo_modello: string | null;
  odoo_id: number | null;
  registrato_da: string | null;
}

export interface Voce {
  codice: string;
  etichetta: string;
  /** `ricavata`: la dice un dato dell'app e non si spunta. */
  fonte: "manuale" | "ricavata";
  fatta: boolean;
  fatta_il: string | null;
  nota: string | null;
  registrata_da: string | null;
  /** Solo passo 8: la voce serve a questa pratica. */
  richiesta: boolean;
  /** Si può spuntare adesso (es. «macchina creata» solo con il device). */
  disponibile: boolean;
}

export interface Passo {
  tipo: TipoPasso;
  numero: string;
  titolo: string;
  gruppo: string;
  ambito: "pratica" | "cassaforte";
  stato: StatoBase;
  stato_intermedio: { codice: string; etichetta: string } | null;
  stati: StatoIntermedio[];
  voci: Voce[];
  in_attesa_di: string | null;
  motivo: string | null;
  non_necessario: { automatico: boolean; motivo: string; da: string | null; at: string | null; pratica_id: string | null } | null;
  da_verificare: string[];
  responsabile: Responsabile | null;
  record: { modulo: ModuloRecord; id: string } | null;
  richiesta_a: string | null;
  nota: string;
  prossima_azione: string | null;
  /** Passi 7 e 9: la bozza precompilata del record, da confermare (PR C1). */
  bozza: { id: string; dal: string; tipo: TipoBozza } | null;
  /** Passi 7 e 9, senza record né bozza: lo scarto che la tiene ferma, da annullare (PR C2). */
  bozza_scartata: { id: string; motivo: string | null; da: string | null; at: string | null; tipo: TipoBozza } | null;
  vigilanza: {
    voci_mancanti: string[];
    scadenza_preavviso: string | null;
    preavviso_non_calcolabile: string | null;
    preavviso_non_rispettato: boolean;
  } | null;
}

export interface Promemoria {
  tipo: "odv" | "oda";
  modulo: ModuloRecord;
  attivita_id: string;
  data: string | null;
  testo: string;
}

export interface DatiOrdine {
  installare_entro: string | null;
  indirizzo: { indirizzo: string | null; cap: string | null; citta: string | null; provincia: string | null };
  interlocutore: { nome: string | null; email: string | null; telefono: string | null };
  /** Solo suggerimenti: diventano l'interlocutore solo con una conferma. */
  suggerimenti_interlocutore: {
    referente_cliente: { nome: string | null; email: string | null; telefono: string | null } | null;
    email_negozio: string | null;
  };
  cartella_url: string | null;
  spunte: { cartella_punto_vendita_il: string | null; scheda_btv_il: string | null; elenco_utenti_il: string | null };
}

export interface DettaglioPratica extends RigaPratica {
  /** Nel dettaglio la cassaforte dice anche se è chiusa, da chi, quando e perché. */
  cassaforte: Cassaforte & { chiusura: { at: string; da: string | null; motivo: string | null } | null };
  passi: Passo[];
  /** I passi che «Sede già attiva…» segnerebbe adesso (PR E1); vuoto: il pulsante non serve. */
  passi_sede_gia_attiva: TipoPasso[];
  promemoria: Promemoria[];
  dati_ordine: DatiOrdine;
  servizio_dichiarato: TipoServizioPrecredit | null;
  servizio_dispositivo: TipoServizioPrecredit | null;
  prima_wink_on_cash: boolean;
  conto_tecnico_dichiarato: string | null;
  valori: { conto_tecnico: string | null; codice_gdo: string | null };
  chiusura: { at: string; da: string | null; motivo: string | null } | null;
  creata_da: string | null;
  contratti_che_citano_la_sede: {
    id: string;
    riferimento: string | null;
    stato: string;
    data_contratto: string | null;
    data_invio: string | null;
  }[];
  /** Nome di chi è citato nella pratica (chi ha registrato, chi ha chiuso…), per id. */
  utenti: Record<string, string>;
  etichette_stato: Record<StatoBase, string>;
}

export async function listPratiche(params: ParametriElenco): Promise<ElencoPratiche> {
  const { data } = await apiClient.get<ElencoPratiche>("/lavori/pratiche", {
    params: {
      stato: params.stato || undefined,
      filtro: params.filtro || undefined,
      servizio: params.servizio || undefined,
      q: params.q?.trim() || undefined,
    },
  });
  return data;
}

export async function getPratica(id: string): Promise<DettaglioPratica> {
  const { data } = await apiClient.get<DettaglioPratica>(`/lavori/pratiche/${id}`);
  return data;
}

/**
 * Il riquadro «Stato lavori» della Vista Sede (B4): le pratiche con questa
 * sede e, per la sede senza pratica, la data di attivazione (decisione Q15):
 * dall'ultima attivazione effettuata con la sede, altrimenti dai dispositivi
 * con una relazione aperta qui. `fonte` dice quale delle due.
 */
export interface StatoLavoriSede {
  pratiche: RigaPratica[];
  attivata_il: { data: string; fonte: "attivazione" | "dispositivo" } | null;
}

export async function getStatoLavoriSede(sedeId: string): Promise<StatoLavoriSede> {
  const { data } = await apiClient.get<StatoLavoriSede>(`/sedi/${sedeId}/stato-lavori`);
  return data;
}

/** Le attività che hanno promemoria: i tre moduli delle pratiche più gli interventi. */
export type ModuloPromemoria = ModuloRecord | "intervento";

/**
 * Un OdV o un OdA mancante, per il riquadro «Da fare» della dashboard: tutte le
 * attività concluse dal 1/10/2026, anche fuori pratica e compresi gli
 * interventi. Le regole le applica il backend
 * (`services/statoLavori/promemoriaOrdini.ts`).
 */
export interface PromemoriaOrdine {
  tipo: "odv" | "oda";
  modulo: ModuloPromemoria;
  attivita_id: string;
  data: string | null;
  testo: string;
  cliente: { id: string; nome: string } | null;
  sede: { id: string; nome: string | null; citta: string | null } | null;
  matricola: string | null;
}

export interface PromemoriaOrdini {
  /** Da quale data contano le attività concluse (`AAAA-MM-GG`). */
  data_inizio: string;
  /** Dalla più vecchia. */
  promemoria: PromemoriaOrdine[];
}

export async function getPromemoriaOrdini(): Promise<PromemoriaOrdini> {
  const { data } = await apiClient.get<PromemoriaOrdini>("/lavori/promemoria-ordini");
  return data;
}

// --- Passi da fare (PR D) -----------------------------------------------------

/** Un passo da fare o da verificare nel «Da fare» della dashboard. */
export interface VoceDaFare {
  pratica_id: string;
  /** NULL: il servizio della pratica da verificare, una voce per pratica. */
  passo: {
    tipo: TipoPasso;
    numero: string;
    titolo: string;
    stato: StatoBase;
    in_attesa_di: string | null;
    prossima_azione: string | null;
  } | null;
  /** Le cose da verificare (senza il servizio, che ha la sua voce). */
  da_verificare: string[];
  responsabile: Responsabile | null;
  cliente: { id: string; nome: string };
  sede: { id: string; nome: string | null; citta: string | null };
  cassaforte: { id: string; matricola: string | null; etichetta: string | null };
  /** Passo di una cassaforte condivisa: le altre pratiche in cui compare. */
  altre_pratiche: { pratica_id: string; cliente: { id: string; nome: string } }[];
}

export interface PassiDaFare {
  /** Passi 1–5, 8 e A con l'utente come responsabile. */
  miei: VoceDaFare[];
  /** Passi 1–5, 8 e A senza responsabile, e il servizio da verificare. */
  senza_responsabile: VoceDaFare[];
  /** Passi 6, 7 e 9, chiunque sia il responsabile. */
  operativi: VoceDaFare[];
  etichette_stato: Record<StatoBase, string>;
}

export async function getPassiDaFare(): Promise<PassiDaFare> {
  const { data } = await apiClient.get<PassiDaFare>("/lavori/passi-da-fare");
  return data;
}

// --- Scritture: ognuna risponde con il dettaglio ricalcolato ------------------

export interface NuovaPratica {
  sede_id: string;
  /** Un dispositivo con relazione aperta su quella sede… */
  device_id?: string | null;
  /** …oppure una cassaforte già in lavorazione, da condividere… */
  lavoro_cassaforte_id?: string | null;
  /** …oppure nessuno dei due: una cassaforte nuova, con un'etichetta facoltativa. */
  etichetta?: string | null;
  tipo_servizio_precredit?: TipoServizioPrecredit | null;
  installare_entro?: string | null;
  interlocutore_operativo_nome?: string | null;
  interlocutore_operativo_email?: string | null;
  interlocutore_operativo_telefono?: string | null;
  /** «Sede già attiva» (PR E1): segna subito «non necessario» i passi preliminari aperti. */
  sede_gia_attiva?: boolean;
}

export async function avviaPratica(payload: NuovaPratica): Promise<DettaglioPratica> {
  const { data } = await apiClient.post<DettaglioPratica>("/lavori/pratiche", payload);
  return data;
}

/** I campi della pratica che si modificano: si mandano solo quelli cambiati. */
export interface ModificaPratica {
  tipo_servizio_precredit?: TipoServizioPrecredit | null;
  installare_entro?: string | null;
  interlocutore_operativo_nome?: string | null;
  interlocutore_operativo_email?: string | null;
  interlocutore_operativo_telefono?: string | null;
  cartella_url?: string | null;
  cartella_punto_vendita_il?: string | null;
  scheda_btv_il?: string | null;
  elenco_utenti_il?: string | null;
  conto_tecnico?: string | null;
}

export async function modificaPratica(id: string, payload: ModificaPratica): Promise<DettaglioPratica> {
  const { data } = await apiClient.patch<DettaglioPratica>(`/lavori/pratiche/${id}`, payload);
  return data;
}

/** Chiude la pratica: il motivo è obbligatorio. Una pratica chiusa non si modifica più. */
export async function chiudiPratica(id: string, motivo: string): Promise<DettaglioPratica> {
  const { data } = await apiClient.post<DettaglioPratica>(`/lavori/pratiche/${id}/chiudi`, { motivo });
  return data;
}

/** Riapre la pratica; con la cassaforte chiusa il backend risponde 409. */
export async function riapriPratica(id: string): Promise<DettaglioPratica> {
  const { data } = await apiClient.post<DettaglioPratica>(`/lavori/pratiche/${id}/riapri`);
  return data;
}

/**
 * Chiude la cassaforte di lavoro, e con lei tutte le sue pratiche: il motivo è
 * obbligatorio. Risponde con le righe delle pratiche, non con il dettaglio.
 */
export async function chiudiCassaforte(cassaforteId: string, motivo: string): Promise<{ pratiche: RigaPratica[] }> {
  const { data } = await apiClient.post<{ pratiche: RigaPratica[] }>(`/lavori/casseforti/${cassaforteId}/chiudi`, { motivo });
  return data;
}

/** Riapre la cassaforte di lavoro: anche qui il motivo è obbligatorio, e va nell'audit. */
export async function riapriCassaforte(cassaforteId: string, motivo: string): Promise<{ pratiche: RigaPratica[] }> {
  const { data } = await apiClient.post<{ pratiche: RigaPratica[] }>(`/lavori/casseforti/${cassaforteId}/riapri`, { motivo });
  return data;
}

/** Chi si può scegliere come responsabile di un passo: admin e staff attivi. */
export interface PersonaResponsabile {
  id: string;
  nome: string;
  ruolo: "admin" | "staff";
}

export async function listResponsabili(): Promise<PersonaResponsabile[]> {
  const { data } = await apiClient.get<{ utenti: PersonaResponsabile[] }>("/lavori/responsabili");
  return data.utenti;
}

/**
 * Associa il dispositivo a una cassaforte di lavoro che non ne ha. Dopo non si
 * cambia né si toglie. Risponde con le righe delle pratiche della cassaforte,
 * non con il dettaglio.
 */
export async function associaDispositivo(cassaforteId: string, deviceId: string): Promise<{ pratiche: RigaPratica[] }> {
  const { data } = await apiClient.patch<{ pratiche: RigaPratica[] }>(`/lavori/casseforti/${cassaforteId}`, { device_id: deviceId });
  return data;
}

const passo = (id: string, tipo: TipoPasso) => `/lavori/pratiche/${id}/passi/${tipo}`;

/** «Avanza a…»: solo allo stato successivo. `valore` per «Aperto» (passo 3) e «Assegnato» (passo 4). */
export async function avanzaPasso(id: string, tipo: TipoPasso, body: { stato: string; il: string; valore?: string }): Promise<DettaglioPratica> {
  const { data } = await apiClient.post<DettaglioPratica>(`${passo(id, tipo)}/avanza`, body);
  return data;
}

/** «Correggi»: riporta il passo a `stato`; `null` toglie tutti gli stati. */
export async function correggiPasso(id: string, tipo: TipoPasso, stato: string | null): Promise<DettaglioPratica> {
  const { data } = await apiClient.post<DettaglioPratica>(`${passo(id, tipo)}/correggi`, { stato });
  return data;
}

export async function segnaNonNecessario(id: string, tipo: TipoPasso, motivo: string): Promise<DettaglioPratica> {
  const { data } = await apiClient.put<DettaglioPratica>(`${passo(id, tipo)}/non-necessario`, { motivo });
  return data;
}

export async function annullaNonNecessario(id: string, tipo: TipoPasso): Promise<DettaglioPratica> {
  const { data } = await apiClient.delete<DettaglioPratica>(`${passo(id, tipo)}/non-necessario`);
  return data;
}

/** «Sede già attiva» (PR E1): i passi preliminari aperti diventano «non necessario». 409 se non ce n'è. */
export async function segnaSedeGiaAttiva(id: string): Promise<DettaglioPratica> {
  const { data } = await apiClient.post<DettaglioPratica>(`/lavori/pratiche/${id}/sede-gia-attiva`);
  return data;
}

/** Il responsabile di un passo (non 6, 7 e 9); `null` lo toglie. */
export async function impostaResponsabile(id: string, tipo: TipoPasso, utenteId: string | null): Promise<DettaglioPratica> {
  const { data } = await apiClient.put<DettaglioPratica>(`${passo(id, tipo)}/responsabile`, { utente_id: utenteId });
  return data;
}

/** Collega a mano il record di un passo 6, 7 o 9; `null` lo scollega. */
export async function collegaRecord(id: string, tipo: TipoPasso, recordId: string | null): Promise<DettaglioPratica> {
  const { data } = await apiClient.put<DettaglioPratica>(`${passo(id, tipo)}/collegamento`, { record_id: recordId });
  return data;
}

export async function spuntaVoce(id: string, tipo: TipoPasso, body: { codice: string; fatta_il: string; nota?: string | null }): Promise<DettaglioPratica> {
  const { data } = await apiClient.post<DettaglioPratica>(`${passo(id, tipo)}/voci`, body);
  return data;
}

export async function togliVoce(id: string, tipo: TipoPasso, codice: string): Promise<DettaglioPratica> {
  const { data } = await apiClient.delete<DettaglioPratica>(`${passo(id, tipo)}/voci/${encodeURIComponent(codice)}`);
  return data;
}

// --- Bozze (PR C1/C2) ----------------------------------------------------------
// Proposte del passo successivo: diventano record solo dal form del modulo, che
// dopo il salvataggio chiama `confermaBozza`. Le regole stanno nel backend
// (`services/statoLavori/bozze.ts`).

/** I campi con cui il form del modulo si precompila. */
export interface DatiBozza {
  cliente_id: string;
  sede_id: string;
  device_id: string | null;
  /** Bozza installazione: il sopralluogo da collegare. */
  sopralluogo_id?: string;
  /** Bozza attivazione: l'installazione a cui si collega. */
  schedule_id?: string;
}

/** Una bozza da confermare, nell'elenco delle pratiche aperte. */
export interface BozzaInElenco {
  id: string;
  tipo: TipoBozza;
  /** Quando è nata (ISO). */
  dal: string;
  cassaforte: Cassaforte;
  /** Installazione: le pratiche aperte della cassaforte; attivazione: la sua. */
  pratiche: { id: string; cliente: { id: string; nome: string }; sede: { id: string; nome: string | null } }[];
  origine: { modulo: "sopralluogo" | "installazione"; id: string };
  dati: DatiBozza;
}

/** Le bozze da confermare di tutte le pratiche aperte, dalla più vecchia. */
export async function getBozze(): Promise<BozzaInElenco[]> {
  const { data } = await apiClient.get<{ bozze: BozzaInElenco[] }>("/lavori/bozze");
  return data.bozze;
}

/** «Scarta», con motivo obbligatorio. */
export async function scartaBozza(id: string, motivo: string): Promise<void> {
  await apiClient.post(`/lavori/bozze/${id}/scarta`, { motivo });
}

/** «Annulla scarto»: 409 se la bozza non è più dovuta. */
export async function annullaScartoBozza(id: string): Promise<void> {
  await apiClient.post(`/lavori/bozze/${id}/annulla-scarto`);
}

/** Dopo il salvataggio del record nel form del modulo: registra chi ha confermato e con quale record. */
export async function confermaBozza(id: string, recordId: string): Promise<void> {
  await apiClient.post(`/lavori/bozze/${id}/conferma`, { record_id: recordId });
}
